import { canonicalCourseSearch } from "@/navigation/course-address";
import { courseWorkspaceSectionHref, type CourseWorkspaceView } from "@/navigation/course-workspace";

export const PROTECTED_ROUTES = {
  admin: {
    dashboard: "/admin",
    admissions: "/admin/admissions",
    // The record pages of the people lane (iteration 5). PARAMETERISED paths,
    // so they are absent from PROTECTED_ROUTE_PATHS; mint them with
    // `registrarApplicantPath`, `registrarPersonPath`, `registrarInvitationPath`.
    applicant: "/admin/admissions/:applicantId",
    people: "/admin/people",
    person: "/admin/people/:personKey",
    invitations: "/admin/invitations",
    invitation: "/admin/invitations/:invitationId",
    inbox: "/admin/inbox",
    academicStructure: "/admin/academic-structure",
    courseHistory: "/admin/course-history",
    historicalImports: "/admin/course-history/imports",
    courses: "/admin/courses",
    // Bring a live Google Classroom course into IMAM from one review (ADR 0037).
    classroomIntake: "/admin/courses/classroom-intake",
    // The registrar course record (iteration 4). A PARAMETERISED path, so it is
    // absent from PROTECTED_ROUTE_PATHS. Views are `?view=` on it
    // (`registrarCoursePath`).
    course: "/admin/courses/:courseId",
    settings: "/admin/settings",
    integrations: "/admin/settings/integrations",
  },
  adminTeaching: {
    drive: "/admin/teaching/drive",
    dashboard: "/admin/teaching",
    inbox: "/admin/teaching/inbox",
    sessions: "/admin/teaching/sessions",
    // Compatibility only: redirects to the Home, which lists every course.
    courses: "/admin/teaching/courses",
    course: "/admin/teaching/courses/:courseId",
    // Compatibility only: retired record pages. Each arrival redirects to the
    // course workspace view that owns the record (`seatRecordDestination`).
    assignments: "/admin/teaching/assignments",
    attendance: "/admin/teaching/attendance",
    gradebook: "/admin/teaching/gradebook",
    announcements: "/admin/teaching/announcements",
    // Issue #321 Milestone 1. A PARAMETERISED path, so it is deliberately
    // absent from PROTECTED_ROUTE_PATHS below, which is a set of exact
    // literals used for navigation matching.
    session: "/admin/teaching/sessions/:sessionId",
  },
  instructor: {
    drive: "/instructor/drive",
    dashboard: "/instructor",
    inbox: "/instructor/inbox",
    sessions: "/instructor/sessions",
    // Compatibility only: redirects to the Home, which lists every course.
    courses: "/instructor/courses",
    course: "/instructor/courses/:courseId",
    faculty: "/instructor/faculty",
    // Compatibility only: retired record pages (`seatRecordDestination`).
    assignments: "/instructor/assignments",
    attendance: "/instructor/attendance",
    gradebook: "/instructor/gradebook",
    announcements: "/instructor/announcements",
    session: "/instructor/sessions/:sessionId",
  },
  student: {
    dashboard: "/student",
    inbox: "/student/inbox",
    sessions: "/student/sessions",
    profile: "/student/profile",
    // Compatibility only: redirects to the Home, which lists every course.
    courses: "/student/courses",
    course: "/student/courses/:courseId",
    faculty: "/student/faculty",
    // Without `course`, the attendance history and the academic record across
    // every course. With `course`, retired record pages like the two below.
    attendance: "/student/attendance",
    grades: "/student/grades",
    // Compatibility only: retired record pages (`seatRecordDestination`).
    assignments: "/student/assignments",
    announcements: "/student/announcements",
    session: "/student/sessions/:sessionId",
  },
} as const;

/** Compatibility entry points. New navigation and record links never emit these paths. */
export const LEGACY_PROTECTED_ROUTES = {
  // Retired registrar addresses. Each arrival redirects to the course record
  // view, the Courses list, the Inbox, Historical Imports, the person record
  // view, or the People reading that owns it (`registrarRecordDestination`).
  admin: {
    courseSetup: "/admin/course-setup",
    enrollment: "/admin/enrollment",
    attendance: "/admin/attendance",
    assignments: "/admin/assignments",
    grades: "/admin/grades",
    announcements: "/admin/announcements",
    imports: "/admin/imports",
    students: "/admin/students",
    accounts: "/admin/accounts",
    faculty: "/admin/faculty",
  },
  // Course sub-paths retired into views of the course workspace.
  adminTeaching: {
    coursePlan: "/admin/teaching/courses/:courseId/plan",
    courseMaterials: "/admin/teaching/courses/:courseId/materials",
  },
  instructor: {
    coursePlan: "/instructor/courses/:courseId/plan",
    courseMaterials: "/instructor/courses/:courseId/materials",
  },
  student: {
    coursePlan: "/student/courses/:courseId/plan",
    courseMaterials: "/student/courses/:courseId/materials",
  },
} as const;

/**
 * Where a valid session with no resolved LMS membership goes.
 *
 * Deliberately outside `PROTECTED_ROUTES`: reaching it requires a session and
 * forbids a role, so the role gate that guards every route above would bounce
 * exactly the people this page exists for.
 */
export const ACCESS_PENDING_ROUTE = "/access-pending";

/** Where an active student member lands until an active enrollment exists. */
export const WORKSPACE_UNAVAILABLE_ROUTE = "/workspace-unavailable";

export const PROTECTED_ROUTE_PATHS = new Set(
  [
    ...Object.values(PROTECTED_ROUTES).flatMap((routes) => Object.values(routes)),
    ...Object.values(LEGACY_PROTECTED_ROUTES).flatMap((routes) => Object.values(routes)),
  ]
    // Parameterised routes are declarations, not destinations: `:sessionId` is
    // never a URL anyone navigates to, and leaving it in this set would make
    // the literal string match a path.
    .filter((path) => !path.includes(":")),
);

export type CourseWorkspaceShell = "adminTeaching" | "instructor" | "student";

/**
 * One course workspace path minter for all three seat shells (issue #542).
 *
 * The Desk is the bare course path; every other view is a `view`
 * parameter on it. A path selects the shell and the view but grants no
 * authority. Every read still rechecks tenant, membership, and the course
 * relationship on the server.
 */
export function courseWorkspacePath(
  shell: CourseWorkspaceShell,
  courseId: string,
  view: CourseWorkspaceView = "desk",
): string {
  return courseWorkspaceSectionHref(
    PROTECTED_ROUTES[shell].course.replace(":courseId", encodeURIComponent(courseId)),
    view,
  );
}

/**
 * One course workspace address for a retired arrival: the named view (an old
 * view name or a current one) leads, every other arrival parameter follows in
 * arrival order, and the hash survives. The query then resolves through the
 * one course resolver, so the first replace is the final address (#1393
 * contract 1, rule 1). A null view keeps the view the arrival named.
 */
function courseWorkspaceDestination(
  shell: CourseWorkspaceShell,
  courseId: string,
  view: string | null,
  params: URLSearchParams,
  hash: string,
): string {
  // The arrival's own view stays beside the named one unless they agree, so
  // the resolver refuses a conflict rather than this adapter choosing (rule 4).
  const query = new URLSearchParams(view ? { view } : undefined);
  for (const [key, value] of params) if (!(view && key === "view" && value === view)) query.append(key, value);
  const search = canonicalCourseSearch(shell !== "student", query);
  return `${courseWorkspacePath(shell, courseId)}${search ? `?${search}` : ""}${hash}`;
}

/**
 * Resolve one legacy seat course-list URL to its course workspace view.
 * `course` becomes the path and `tab` the view; every other parameter survives
 * so contextual handoffs such as `unit` and `assignment` keep their identity.
 * On a teaching seat native work opens in its Coursework row (ADR 0035), so a
 * link that carries `assignment` lands there with the work selected, whatever
 * its tab; a Classroom record (`owner`) is a sheet over Desk (R29, R36).
 */
export function legacyCourseWorkspaceDestination(
  shell: CourseWorkspaceShell,
  search: string,
): string | null {
  const params = new URLSearchParams(search);
  const courseId = params.get("course");
  if (!courseId) return null;

  // A duplicated or empty tab is malformed: it stays for the resolver to
  // refuse, never resolved to its first value (rule 4, M4-A4).
  const tabs = params.getAll("tab");
  const tab = tabs.length === 1 && tabs[0] !== "" ? tabs[0] : null;
  params.delete("course");
  if (tab !== null) params.delete("tab");
  // A teacher's exact work outranks the tab, whose view is then never named:
  // the resolver opens the work in its Coursework row (R34, R52-R61).
  const teachingWork = shell !== "student" && params.has("assignment") && !params.has("owner");
  return courseWorkspaceDestination(
    shell,
    courseId,
    teachingWork ? null : tab === "materials" ? "materials" : tab === "course-plan" ? "plan" : null,
    params,
    "",
  );
}

/** The course workspace view that owns each retired seat record page. */
const RETIRED_RECORD_VIEWS = new Map<string, CourseWorkspaceView>([
  ["assignments", "coursework"],
  ["attendance", "people"],
  ["gradebook", "grades"],
  ["grades", "grades"],
  ["announcements", "notices"],
]);

/** Course sub-paths retired into Desk: Calendar order and the Resources part (R19, R20). */
const RETIRED_COURSE_PATHS = new Set(["plan", "materials"]);

/**
 * Attendance arrivals by shell (#1393 R1): a learner's own attendance is a part
 * of Grades; a teacher's roster and roll are the attendance part of People.
 * Either way the arrival names the attendance part, replacing any other.
 */
function attendanceArrival(shell: CourseWorkspaceShell, view: CourseWorkspaceView, params: URLSearchParams): CourseWorkspaceView {
  if (view !== "people") return view;
  params.delete("part");
  params.set("part", "attendance");
  return shell === "student" ? "grades" : "people";
}

function decodeSegment(segment: string): string | null {
  try {
    return decodeURIComponent(segment);
  } catch {
    return null;
  }
}

/**
 * Where an arrival at a retired seat address belongs, or null when the arrival
 * is itself a destination.
 *
 * Every course-scoped record has one address: a view of the course workspace,
 * with the record selected by a query parameter. The generation 1 forms
 * (`{base}/assignments?course=`, `{base}/attendance?course=`,
 * `{base}/gradebook?course=`, `/student/grades?course=`,
 * `{base}/announcements`, `{base}/courses?course=&tab=`,
 * `{base}/courses/X/plan|materials`) stay routable so bookmarks, emails, and
 * journeys keep landing, and this is the one place that knows them; the
 * workspace's own query (`?open=`, retired views) is `course-address.ts`'s.
 * Every other parameter and the hash survive.
 *
 * Without a course, the learner's attendance history and academic record are
 * destinations of their own, course notices fall back to institution notices,
 * and every other retired page lands on the Home.
 */
export function seatRecordDestination(
  shell: CourseWorkspaceShell,
  pathname: string,
  search: string,
  hash = "",
): string | null {
  const routes = PROTECTED_ROUTES[shell];
  const prefix = `${routes.dashboard}/`;
  if (!pathname.startsWith(prefix)) return null;
  const segments = pathname.slice(prefix.length).replace(/\/+$/, "").split("/");
  const [family, courseSegment, coursePath] = segments;
  const params = new URLSearchParams(search);

  if (family === "courses") {
    if (segments.length === 1) {
      const destination = legacyCourseWorkspaceDestination(shell, search);
      return destination ? `${destination}${hash}` : routes.dashboard;
    }
    const courseId = decodeSegment(courseSegment);
    // The course's own address (with `?open=` and every retired view) resolves
    // in the workspace, on every location, through `course-address.ts`.
    if (!courseId || segments.length !== 3 || !RETIRED_COURSE_PATHS.has(coursePath)) return null;
    return courseWorkspaceDestination(shell, courseId, coursePath, params, hash);
  }

  const view = RETIRED_RECORD_VIEWS.get(family);
  if (!view || segments.length !== 1 || !Object.hasOwn(routes, family)) return null;
  const courseId = params.get("course");
  params.delete("course");
  if (courseId) return courseWorkspaceDestination(shell, courseId, attendanceArrival(shell, view, params), params, hash);
  if (family === "announcements") {
    params.delete("view");
    const query = params.toString();
    return `${routes.inbox}?view=notices${query ? `&${query}` : ""}${hash}`;
  }
  if (shell === "student" && (family === "attendance" || family === "grades")) return null;
  return routes.dashboard;
}

/** The views of one registrar course record, in tab order. `record` is the bare path. */
export const REGISTRAR_COURSE_VIEWS = ["desk", "record", "roster", "attendance", "coursework", "grades"] as const;
export type RegistrarCourseView = (typeof REGISTRAR_COURSE_VIEWS)[number];

export function isRegistrarCourseView(value: string | null): value is RegistrarCourseView {
  return value !== null && (REGISTRAR_COURSE_VIEWS as readonly string[]).includes(value);
}

/**
 * The one address of a registrar course record and its views (iteration 4):
 * `/admin/courses/{courseId}` with `?view=roster|attendance|coursework|grades`.
 * Record selectors (`session`, `assignment`, `enrollment`) are further query
 * parameters on it. A path selects the view and grants no authority.
 */
export function registrarCoursePath(courseId: string, view: RegistrarCourseView = "desk"): string {
  const path = PROTECTED_ROUTES.admin.course.replace(":courseId", encodeURIComponent(courseId));
  return view === "desk" ? path : `${path}?view=${view}`;
}

/**
 * The views of one person record, in tab order (iteration 5). `record` is the
 * bare path. A person offers `access`, `student`, and `teaching` only when the
 * record holds that relationship; the page replaces any other with `record`.
 */
export const REGISTRAR_PERSON_VIEWS = ["record", "access", "student", "teaching"] as const;
export type RegistrarPersonView = (typeof REGISTRAR_PERSON_VIEWS)[number];

export function isRegistrarPersonView(value: string | null): value is RegistrarPersonView {
  return value !== null && (REGISTRAR_PERSON_VIEWS as readonly string[]).includes(value);
}

/**
 * The one address of a person record and its views: `/admin/people/{personId}`
 * with `?view=access|student|teaching` (ADR 0034). A retired key, `user:{id}`
 * or `student:{id}`, keeps its colon literal; the record page resolves it and
 * replaces it with the person id. A path selects the view and grants no
 * authority.
 */
export function registrarPersonPath(personKey: string, view: RegistrarPersonView = "record"): string {
  const key = personKey.split(":").map(encodeURIComponent).join(":");
  const path = PROTECTED_ROUTES.admin.person.replace(":personKey", key);
  return view === "record" ? path : `${path}?view=${view}`;
}

/**
 * The student view of whoever holds a student record, for a caller that knows
 * only the record's id. The address names the record (`student:{id}`); the
 * record page reads the person who holds it and replaces the address with the
 * person id. This is the one place outside the redirect table that writes a
 * retired person key.
 */
export function registrarStudentRecordPath(studentRecordId: string): string {
  return registrarPersonPath(`student:${studentRecordId}`, "student");
}

/** The one address of an applicant file: `/admin/admissions/{applicantId}`. */
export function registrarApplicantPath(applicantId: string): string {
  return PROTECTED_ROUTES.admin.applicant.replace(":applicantId", encodeURIComponent(applicantId));
}

/** The one address of an invitation record: `/admin/invitations/{invitationId}`. */
export function registrarInvitationPath(invitationId: string): string {
  return PROTECTED_ROUTES.admin.invitation.replace(":invitationId", encodeURIComponent(invitationId));
}

/**
 * History state a list row carries into the record it opens: the list address
 * as the reader left it, so the record's way back restores the same reading.
 */
export interface RegistrarListReturnState {
  registrarList?: string;
}

/**
 * The record's way back: the list address carried in history state when it is
 * that list (`list` itself or `list?…`), otherwise the bare list. Anything else
 * in state is ignored, so a forged or stale value can never send the reader
 * somewhere other than the list.
 */
export function registrarListHref(state: unknown, list: string): string {
  const href = (state as RegistrarListReturnState | null)?.registrarList;
  return typeof href === "string" && (href === list || href.startsWith(`${list}?`)) ? href : list;
}

/** The course record view that owns each retired registrar page. */
const RETIRED_REGISTRAR_VIEWS = new Map<string, RegistrarCourseView>([
  [LEGACY_PROTECTED_ROUTES.admin.courseSetup, "roster"],
  [LEGACY_PROTECTED_ROUTES.admin.enrollment, "roster"],
  [LEGACY_PROTECTED_ROUTES.admin.attendance, "attendance"],
  [LEGACY_PROTECTED_ROUTES.admin.assignments, "coursework"],
  [LEGACY_PROTECTED_ROUTES.admin.grades, "grades"],
]);

/** A destination that keeps every other arrival parameter, in arrival order, and the hash. */
function withArrivalState(path: string, params: URLSearchParams, hash: string): string {
  const query = params.toString();
  return `${path}${query ? `${path.includes("?") ? "&" : "?"}${query}` : ""}${hash}`;
}

/** The People list's filters: list state that a person record does not keep. */
const PEOPLE_LIST_STATE = [
  "view", "student_view", "relationship", "access", "attention", "program_id", "cohort_id",
  "student_status", "teaching", "q", "page", "page_size",
] as const;

/** The Admissions list's reading: list state that an applicant file does not keep. */
const ADMISSIONS_LIST_STATE = ["stage", "program", "q", "sort", "dir"] as const;

/** The Invitations list's reading: list state that an invitation record does not keep. */
const INVITATIONS_LIST_STATE = ["view", "q", "page"] as const;

type RetiredDirectory = {
  /** The parameter that named one record on the retired page. */
  parameter: string;
  /**
   * The retired key one id becomes and the view it opens, or null when only a
   * lookup can name the person. The record page replaces the key with the
   * person id.
   */
  record: { keyPrefix: "student" | "user"; view: RegistrarPersonView } | null;
  /** The People reading the bare directory lands on. */
  reading: string;
};

/** The retired People directories, each with the person record view that owns its records. */
const RETIRED_DIRECTORIES = new Map<string, RetiredDirectory>([
  [LEGACY_PROTECTED_ROUTES.admin.students, {
    parameter: "student",
    record: { keyPrefix: "student", view: "student" },
    reading: "relationship=student&student_view=current",
  }],
  [LEGACY_PROTECTED_ROUTES.admin.accounts, {
    parameter: "account",
    record: { keyPrefix: "user", view: "access" },
    reading: "access=active&access=deactivated",
  }],
  [LEGACY_PROTECTED_ROUTES.admin.faculty, {
    parameter: "faculty",
    record: null,
    reading: "relationship=faculty",
  }],
]);

/** A person record view that keeps every other arrival parameter, in arrival order, and the hash. */
function personRecordDestination(
  personKey: string,
  view: RegistrarPersonView,
  params: URLSearchParams,
  hash: string,
): string {
  params.delete("view");
  return withArrivalState(registrarPersonPath(personKey, view), params, hash);
}

/**
 * Where a retired Faculty bookmark lands once its profile id resolves to a
 * person id: that person's teaching view. `registrarRecordDestination` leaves
 * this arrival to the lookup because only the server can name the person.
 */
export function legacyFacultyDestination(personKey: string, search: string, hash = ""): string {
  const params = new URLSearchParams(search);
  params.delete("faculty");
  return personRecordDestination(personKey, "teaching", params, hash);
}

/**
 * Where an arrival at a retired registrar address belongs, or null when the
 * arrival is itself a destination (iterations 4 and 5).
 *
 * A course is the path: the record and each of its views live at
 * `registrarCoursePath`. So is a person (`registrarPersonPath`), an applicant
 * file (`registrarApplicantPath`), and an invitation
 * (`registrarInvitationPath`). The earlier forms (`/admin/courses?course=`,
 * Course Setup and its `/admin/enrollment` alias, the Attendance, Assignments
 * and Grades section pickers, `/admin/announcements`, `/admin/imports/*`, a
 * record selected over the People, Admissions, or Invitations list, and the
 * Students, Accounts, and Faculty directories) stay routable so bookmarks,
 * emails, and journeys keep landing, and this is the one place that knows
 * them. Every other parameter and the hash survive; a list's own reading does
 * not follow a record off its list.
 *
 * Without a course, a retired page lands on the Courses list; Course Setup's
 * create mode opens the guided create flow there. Historical Imports keeps its
 * path suffix and its query exactly. A directory without an id lands on its
 * People reading. A Faculty id names a profile, not a person, so that arrival
 * is left to the lookup (`legacyFacultyDestination`).
 */
export function registrarRecordDestination(pathname: string, search: string, hash = ""): string | null {
  const path = pathname.replace(/\/+$/, "");
  const params = new URLSearchParams(search);
  const retired = LEGACY_PROTECTED_ROUTES.admin;

  if (path === PROTECTED_ROUTES.admin.people) {
    const personKey = params.get("person");
    if (!personKey) return null;
    params.delete("person");
    for (const name of PEOPLE_LIST_STATE) params.delete(name);
    return withArrivalState(registrarPersonPath(personKey), params, hash);
  }

  if (path === PROTECTED_ROUTES.admin.admissions) {
    const applicantId = params.get("applicant");
    if (!applicantId) return null;
    params.delete("applicant");
    for (const name of ADMISSIONS_LIST_STATE) params.delete(name);
    return withArrivalState(registrarApplicantPath(applicantId), params, hash);
  }

  if (path === PROTECTED_ROUTES.admin.invitations) {
    const invitationId = params.get("invitation");
    if (!invitationId) return null;
    params.delete("invitation");
    for (const name of INVITATIONS_LIST_STATE) params.delete(name);
    return withArrivalState(registrarInvitationPath(invitationId), params, hash);
  }

  const directory = RETIRED_DIRECTORIES.get(path);
  if (directory) {
    const id = params.get(directory.parameter);
    if (id) {
      if (!directory.record) return null;
      params.delete(directory.parameter);
      return personRecordDestination(`${directory.record.keyPrefix}:${id}`, directory.record.view, params, hash);
    }
    params.delete(directory.parameter);
    for (const name of new URLSearchParams(directory.reading).keys()) params.delete(name);
    return withArrivalState(`${PROTECTED_ROUTES.admin.people}?${directory.reading}`, params, hash);
  }

  if (path === PROTECTED_ROUTES.admin.courses) {
    const courseId = params.get("course");
    if (!courseId) return null;
    params.delete("course");
    params.delete("status");
    return withArrivalState(registrarCoursePath(courseId), params, hash);
  }

  const view = RETIRED_REGISTRAR_VIEWS.get(path);
  if (view) {
    if (view === "roster" && params.get("mode") === "create") {
      params.delete("mode");
      params.delete("course");
      params.delete("create");
      return withArrivalState(`${PROTECTED_ROUTES.admin.courses}?create=section`, params, hash);
    }
    const courseId = params.get("course");
    params.delete("course");
    if (!courseId) return withArrivalState(PROTECTED_ROUTES.admin.courses, params, hash);
    params.delete("view");
    return withArrivalState(registrarCoursePath(courseId, view), params, hash);
  }

  if (path === retired.announcements) return `${PROTECTED_ROUTES.admin.inbox}${search}${hash}`;
  if (path === retired.imports || path.startsWith(`${retired.imports}/`)) {
    return `${PROTECTED_ROUTES.admin.historicalImports}${path.slice(retired.imports.length)}${search}${hash}`;
  }
  return null;
}
